import { BackendServiceUrl } from './BaseService';
import { httpInterceptor } from './HttpInterceptorService';

export interface StreamingCallbacks {
    onToken: (content: string) => void;
    onAgentStart?: (agentName: string) => void;
    onAgentToken?: (agentName: string, content: string) => void;
    onAgentDone?: (agentName: string) => void;
    onStatus?: (label: string, agent?: string) => void;
    onDone?: (citations: Citation[], module: string, title?: string, messageId?: string) => void;
    onError?: (error: string) => void;
}

export interface Citation {
    title?: string;
    url?: string;
    content?: string;
}

interface StreamEvent {
    type: string;
    content?: string;
    agent?: string;
    citations?: Citation[];
    module?: string;
    title?: string;
    messageId?: string;
}

export class StreamingChatService {
    private readonly serviceUrl: string;

    constructor(serviceUrl: string = BackendServiceUrl) {
        // Remove trailing slash to prevent double-slash in URL paths
        this.serviceUrl = serviceUrl.replace(/\/$/, '');
    }

    async streamBotResponse(
        chatId: string,
        agentId: string,
        message: string,
        accessToken: string,
        callbacks: StreamingCallbacks,
        signal?: AbortSignal,
    ): Promise<void> {
        const effectiveToken = accessToken || httpInterceptor.getCurrentToken();

        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
        };

        if (effectiveToken) {
            headers.Authorization = `Bearer ${effectiveToken}`;
        }

        const interceptorHeaders = httpInterceptor.getAuthHeaders();
        Object.entries(interceptorHeaders).forEach(([key, value]) => {
            if (key !== 'Authorization' || !effectiveToken) {
                headers[key] = value;
            }
        });

        const response = await fetch(`${this.serviceUrl}/chats/${agentId}/${chatId}/stream-messages`, {
            method: 'POST',
            headers,
            body: JSON.stringify({ input: message }),
            signal,
        });

        if (!response.ok) {
            throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const body = response.body;
        if (!body) {
            throw new Error('ReadableStream not supported');
        }

        const reader = body.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        let activeAgent: string | null = null;

        try {
            // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
            while (true) {
                const { done, value } = await reader.read();
                if (done) break;

                buffer += decoder.decode(value, { stream: true });
                const lines = buffer.split('\n');
                buffer = lines.pop() ?? '';

                for (const line of lines) {
                    const trimmed = line.trim();
                    if (!trimmed) continue;

                    if (trimmed === '[DONE]' || trimmed === 'data: [DONE]' || trimmed === 'data:[DONE]') return;

                    const jsonStr = trimmed.startsWith('data:') 
                        ? trimmed.slice(trimmed.indexOf(':') + 1).trim()
                        : trimmed;

                    try {
                        const event = JSON.parse(jsonStr) as StreamEvent;
                        // console.log('[SSE]', event.type, event);
                        switch (event.type) {
                            case 'token':
                                callbacks.onToken(event.content ?? '');
                                // Small delay to force React to render each token individually (word-by-word animation)
                                await new Promise<void>((r) => setTimeout(r, 10));
                                break;
                            case 'agent-start':
                                activeAgent = event.agent ?? null;
                                callbacks.onAgentStart?.(event.agent ?? 'unknown');
                                break;
                            case 'agent-token':
                                callbacks.onAgentToken?.(event.agent ?? activeAgent ?? 'unknown', event.content ?? '');
                                break;
                            case 'agent-done':
                                activeAgent = null;
                                callbacks.onAgentDone?.(event.agent ?? 'unknown');
                                break;
                            case 'status':
                                // Progress narration for the silent gap before the first token.
                                // No artificial delay — these should appear immediately.
                                callbacks.onStatus?.(event.content ?? '', event.agent);
                                break;
                            case 'done':
                                callbacks.onDone?.(event.citations ?? [], event.module ?? '', event.title, event.messageId);
                                break;
                            case 'error':
                                callbacks.onError?.(event.content ?? '');
                                break;
                            default:
                                console.warn('[SSE] Unknown event type:', event.type, event);
                        }
                    } catch {
                        // JSON parse error — skip this line
                    }
                }
            }
        } finally {
            reader.releaseLock();
        }
    }
}
